
import { supabase } from "@/integrations/supabase/client";
import { Movie } from "@/types/movie";
import { toRpcPayload } from "@/utils/moviePayloads";
import { SINGLE_LIST_TYPE, type SingleListType } from "@/hooks/search/types/listConstants";

/**
 * Service for single-list operations using add_to_single_list and remove_from_single_list RPCs
 * UPDATED: Now ensures complete metadata payload is always passed for consistency
 */
export class SingleListService {
  
  /**
   * Add a movie to a single list (favorite or plan_to_watch) using add_to_single_list RPC
   * UPDATED: Always passes complete metadata payload for universal consistency
   */
  static async addToList(
    movie: Movie,
    listType: SingleListType
  ): Promise<{ success: boolean; error?: any }> {
    try {
      console.log(`🔍 [SINGLE-LIST] Adding ${movie.title} to ${listType} list with complete metadata`);
      
      // Use RPC payload to get all the metadata fields in consistent format
      const payload = toRpcPayload(movie);
      
      const { error } = await supabase.rpc('add_to_single_list', {
        p_tmdb_id: payload.tmdb_id,
        p_list_type: listType,
        p_title: payload.title,
        p_director: payload.director,
        p_year: payload.year,
        p_poster_path: payload.poster_path,
        p_backdrop_path: payload.backdrop_path,
        p_overview: payload.overview,
        p_tags: payload.tags,
        // UPDATED: Always include complete metadata for universal consistency
        p_release_date: payload.release_date,
        p_vote_average: payload.vote_average,
        p_runtime: payload.runtime,
        p_popularity: payload.popularity,
        p_genres: payload.genres
      });

      if (error) {
        console.error(`❌ [SINGLE-LIST] Error adding to ${listType} with complete metadata:`, error);
        return { success: false, error };
      }

      console.log(`✅ [SINGLE-LIST] Successfully added ${movie.title} to ${listType} list with complete metadata`);
      return { success: true };
    } catch (error) {
      console.error(`❌ [SINGLE-LIST] Exception adding to ${listType}:`, error);
      return { success: false, error };
    }
  }

  /**
   * Remove a movie from a single list (favorite or plan_to_watch) using remove_from_single_list RPC
   * NOTE: This preserves metadata in the global tables while removing from user lists
   */
  static async removeFromList(
    tmdbId: number,
    listType: SingleListType
  ): Promise<{ success: boolean; error?: any }> {
    try {
      console.log(`🔍 [SINGLE-LIST] Removing movie ${tmdbId} from ${listType} list (preserving global metadata)`);
      
      const { error } = await supabase.rpc('remove_from_single_list', {
        p_tmdb_id: tmdbId,
        p_list_type: listType
      });

      if (error) {
        console.error(`❌ [SINGLE-LIST] Error removing from ${listType}:`, error);
        return { success: false, error };
      }

      console.log(`✅ [SINGLE-LIST] Successfully removed movie ${tmdbId} from ${listType} list`);
      return { success: true };
    } catch (error) {
      console.error(`❌ [SINGLE-LIST] Exception removing from ${listType}:`, error);
      return { success: false, error };
    }
  }
}
